import Button from '@components/common/Button/Button.component'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import React, { useState, useEffect } from 'react'
import { useAppSelector } from '@store/hooks'
import { Patient } from '@dataTypes/PatientInfo.types'
import { GetInsurancePlansByPatientId } from '@services/api/insurance.api'
import { useNavigate } from 'react-router-dom'
import { getFullName } from '@utils/fullname.util'
import { mockInsurancePlans } from '@assets/mockdata/PatientTray/mockInsurance'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import { ReactComponent as EyeViewIcon } from '@assets/images/Sidepanel/eye-view.svg'
import moment from 'moment'
import { Badge, type BadgeColor, type BadgeMode } from '@enamelai/design-system'
import { useTheme } from '@context/ThemeContext'
import {
  SkeletonBar,
  SkeletonCard,
  SkeletonList,
  SkeletonRow,
} from './SidePanelSkeleton'

type InsuranceProps = {
  patient: Patient | null
}

const InsuranceSkeleton: React.FC = () => (
  <SkeletonList>
    {[0, 1].map((key) => (
      <SkeletonCard key={key}>
        <SkeletonRow>
          <SkeletonBar width="50%" />
          <SkeletonBar width={56} height={20} pill />
        </SkeletonRow>
        <SkeletonRow>
          <SkeletonBar width="35%" />
          <SkeletonBar width="30%" />
        </SkeletonRow>
        <SkeletonRow>
          <SkeletonBar width="40%" />
          <SkeletonBar width="25%" />
        </SkeletonRow>
      </SkeletonCard>
    ))}
  </SkeletonList>
)

const formatMaxCoverage = (value?: number | string | null): string => {
  if (value == null) return '—'
  const num =
    typeof value === 'string'
      ? parseFloat(value.replace(/[^0-9.]/g, ''))
      : value
  if (Number.isNaN(num)) return '—'
  return `$ ${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatPlanName = (plan: InsurancePlan): string => {
  if (plan.plan_type) {
    return `${plan.plan_name} (${plan.plan_type})`
  }
  return plan.plan_name
}

const getValidityDisplay = (
  plan: InsurancePlan,
  patient: Patient | null,
): string => {
  if (plan.validity) return plan.validity
  if (patient?.insurance_expiry_date) {
    return moment(patient.insurance_expiry_date).format('MM-DD-YYYY')
  }
  return '—'
}

const getInsuranceStatusColor = (status: string): BadgeColor =>
  status === 'Active' ? 'vivid-green' : 'gray'

const InsuranceField: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <div className="sp-insurance-field">
    <span className="sp-insurance-field-label">{label}</span>
    <div className="sp-insurance-field-value">{children}</div>
  </div>
)

const InsurancePlanCard: React.FC<{
  plan: InsurancePlan
  patient: Patient | null
  badgeMode: BadgeMode
  onVerify: () => void
}> = ({ plan, patient, badgeMode, onVerify }) => (
  <div className="sp-insurance-plan">
    <div className="sp-insurance-row">
      <InsuranceField label="Subscriber">
        {plan.subscriber_fullname}
      </InsuranceField>
      <InsuranceField label="Carrier">{plan.carrier_name}</InsuranceField>
    </div>
    <div className="sp-insurance-row">
      <InsuranceField label="Plan">{formatPlanName(plan)}</InsuranceField>
      <InsuranceField label="Validity">
        <div className="sp-insurance-validity-row">
          <span>{getValidityDisplay(plan, patient)}</span>
          <button
            type="button"
            className="sp-insurance-verify-link"
            onClick={onVerify}
          >
            Verify
          </button>
        </div>
      </InsuranceField>
    </div>
    <div className="sp-insurance-row">
      <InsuranceField label="Max. Coverage">
        {formatMaxCoverage(plan.max_coverage)}
      </InsuranceField>
      <InsuranceField label="Status">
        <div className="sp-insurance-status-badges">
          <Badge
            label={plan.status}
            color={getInsuranceStatusColor(plan.status)}
            mode={badgeMode}
          />
          {plan.is_primary && (
            <Badge label="Primary" color="teal" mode={badgeMode} />
          )}
        </div>
      </InsuranceField>
    </div>
  </div>
)

const InsuranceSectionHeader: React.FC<{
  onViewClaims: () => void
}> = ({ onViewClaims }) => (
  <div className="sp-insurance-section-header">
    <span className="sp-insurance-section-title">Insurance plan overview</span>
    <button
      type="button"
      className="sp-insurance-view-btn"
      onClick={onViewClaims}
      aria-label="View claims"
    >
      <EyeViewIcon className="sp-insurance-view-icon" aria-hidden />
    </button>
  </div>
)

const Insurance: React.FC<InsuranceProps> = ({ patient }) => {
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const { refresh } = useAppSelector((state) => state.patient)
  const [insurancePlans, setInsurancePlans] = useState<InsurancePlan[]>([])
  const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const toggleVerifyCoverage = (): void => {
    setShowVerifyModal(!showVerifyModal)
  }

  useEffect(() => {
    if (!patient?.patient_id) return
    const useMockData = shouldUsePatientTrayMockData(patient.patient_id)

    if (useMockData) {
      const mockPlans = mockInsurancePlans[patient.patient_id] || []
      setInsurancePlans(mockPlans)
    } else {
      setIsLoading(true)
      GetInsurancePlansByPatientId(patient.patient_id)
        .then((response) => {
          const plans = response?.data?.insurancePlans || []
          const normalizedPlans = plans.map((plan) => ({
            ...plan,
            is_primary: plan.is_primary ?? false,
          }))
          setInsurancePlans(normalizedPlans)
        })
        .finally(() => setIsLoading(false))
    }
  }, [patient?.patient_id, refresh])

  const orderedPlans = [...insurancePlans].sort((a, b) => {
    if (a.is_primary === b.is_primary) return 0
    return a.is_primary ? -1 : 1
  })
  const [primaryPlan, ...additionalPlans] = orderedPlans

  return (
    <>
      <div className="sp-insurance">
        {isLoading ? (
          <InsuranceSkeleton />
        ) : insurancePlans.length === 0 ? (
          <p className="sp-insurance-empty">No records to display</p>
        ) : (
          <>
            {primaryPlan && (
              <div className="sp-insurance-section sp-insurance-section--first">
                <InsuranceSectionHeader
                  onViewClaims={() => navigate('/claims')}
                />
                <InsurancePlanCard
                  plan={primaryPlan}
                  patient={patient}
                  badgeMode={badgeMode}
                  onVerify={toggleVerifyCoverage}
                />
              </div>
            )}
            {additionalPlans.map((plan) => (
              <InsurancePlanCard
                key={plan.insurance_plan_id ?? plan.subscriber_member_id}
                plan={plan}
                patient={patient}
                badgeMode={badgeMode}
                onVerify={toggleVerifyCoverage}
              />
            ))}
          </>
        )}
      </div>

      <ConfirmationModal
        show={showVerifyModal}
        onHide={toggleVerifyCoverage}
        title="Insurance verification"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Coverage is valid</p>
            <p className="confirmation-modal__message">
              The insurance for {getFullName(patient, true)} is valid.
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              size="md"
              className="button-primary"
              onClick={toggleVerifyCoverage}
            >
              Got it
            </Button>
          </div>
        }
      />
    </>
  )
}

export default Insurance
